import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { dateDiff } from "../lib/datediff";

type Mode = "difference" | "countdown";

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function tabClass(active: boolean) {
  return `rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
    active
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:text-foreground"
  }`;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-card/40 p-4">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 font-display text-xl font-semibold tabular-nums">
        {value}
      </dd>
    </div>
  );
}

export function DateDiffCalc() {
  const uid = useId();
  const [mode, setMode] = useState<Mode>("difference");
  const [start, setStart] = useState("2024-01-01");
  const [end, setEnd] = useState("2024-12-31");
  const [target, setTarget] = useState("");
  const locale = getLocale();

  const result = useMemo(() => {
    if (mode === "difference") return dateDiff(start, end);
    return target ? dateDiff(todayISO(), target) : null;
  }, [mode, start, end, target]);

  const fmt = (n: number) => n.toLocaleString(locale);

  const headline =
    result === null
      ? null
      : mode === "countdown"
        ? result.direction === "future"
          ? m.dd_until({ days: fmt(result.totalDays) })
          : result.direction === "past"
            ? m.dd_ago({ days: fmt(result.totalDays) })
            : m.dd_today()
        : m.dd_days_count({ days: fmt(result.totalDays) });

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
      <div className="mb-6 inline-flex gap-1 rounded-lg border border-border p-1">
        <button
          type="button"
          aria-pressed={mode === "difference"}
          onClick={() => setMode("difference")}
          className={tabClass(mode === "difference")}
        >
          {m.dd_mode_difference()}
        </button>
        <button
          type="button"
          aria-pressed={mode === "countdown"}
          onClick={() => setMode("countdown")}
          className={tabClass(mode === "countdown")}
        >
          {m.dd_mode_countdown()}
        </button>
      </div>

      {mode === "difference" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-start`}>{m.dd_start()}</Label>
            <Input
              id={`${uid}-start`}
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-end`}>{m.dd_end()}</Label>
            <Input
              id={`${uid}-end`}
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-target`}>{m.dd_target()}</Label>
            <Input
              id={`${uid}-target`}
              type="date"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="mt-6" aria-live="polite">
        {result === null || headline === null ? (
          <p className="text-sm text-muted-foreground">{m.dd_enter()}</p>
        ) : (
          <div className="space-y-6">
            <div className="rounded-xl bg-primary/10 p-5 text-center">
              <p
                data-testid="dd-headline"
                className="font-display text-2xl font-bold tabular-nums text-primary sm:text-3xl"
              >
                {headline}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {m.dd_ymd({
                  years: String(result.years),
                  months: String(result.months),
                  days: String(result.days),
                })}
              </p>
            </div>
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat label={m.dd_weeks()} value={fmt(result.totalWeeks)} />
              <Stat label={m.dd_months()} value={fmt(result.totalMonths)} />
              <Stat label={m.dd_total_days()} value={fmt(result.totalDays)} />
              <Stat label={m.dd_weekdays()} value={fmt(result.weekdays)} />
              <Stat label={m.dd_weekend()} value={fmt(result.weekendDays)} />
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
